import {
    CALL,
    EXTEND,
    IF,
    IS,
    IS_NOT,
    LooseObject,
    nil,
    NO,
    UIColor,
    UICore,
    UILink,
    UIRoute,
    UITextView,
    UIView,
    UIViewBroadcastEvent,
    YES
} from "uicore-ts"
import { CompanyViewController } from "./CompanyView/CompanyViewController"
import { CompanyRoleBadgesView } from "./CompanyView/CompanyAdministration/CompanyRoleBadgesView"
import { ContractsViewController } from "./ContractsView/ContractsViewController"
import { type RHApplicationMode, RHCore } from "./Core/RHCore"
import { CBButtonGroup } from "./Custom components/CB/CBButtonGroup"
import { CBColor } from "./Custom components/CB/CBColor"
import { RHFocusManager } from "./Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "./Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHBarButton } from "./Custom components/RH/RHBarButton"
import { RHBarLinkButton } from "./Custom components/RH/RHBarLinkButton"
import { RHColor } from "./Custom components/RH/RHColor"
import { RHNotificationBadge } from "./Custom components/RH/RHNotificationBadge"
import { RHButtons, RHColors, RHFont, RHNavbar, RHSpacing } from "./Custom components/RH/Utils_slop/RHDesignTokens"
import { labelForRHViewport } from "./Custom components/RH/Utils_slop/RHResponsiveText"
import { RHTextSanitizer } from "./Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHResponsiveTextTokens } from "./Custom components/RH/Utils_slop/RHViewTokens"
import { LogViewController } from "./EventLog/LogViewController"
import { FeedbackViewController } from "./FeedbackViewController"
import { HelpViewController } from "./HelpViewController"
import { InvoicesViewController } from "./Invoices/InvoicesViewController"
import { LoginViewController } from "./Login/LoginViewController"
import { NewProcurementPreviewViewController } from "./NewProcurement/NewProcurementPreviewViewController"
import { NewProcurementViewController } from "./NewProcurement/NewProcurementViewController"
import { IncomingOffersViewController } from "./Offers/IncomingOffersViewController"
import { OutgoingOffersViewController } from "./Offers/OutgoingOffersViewController"
import { MyOngoingProcurementsViewController } from "./OngoingProcurements/MyOngoingProcurementsViewController"
import { PublishedProcurementsViewController } from "./OngoingProcurements/PublishedProcurementsViewController"
import { ProcurementDetailsViewController } from "./ProcurementDetails/ProcurementDetailsViewController"
import { ProcurementPlanViewController } from "./ProcurementPlan/ProcurementPlanViewController"
import { ProcurementSearchViewController } from "./Search/ProcurementSearchViewController"
import { SettingsViewController } from "./Settings/SettingsViewController"
import { StatisticsMainViewController } from "./StatisticsMainViewController"
import { TopBarUserMenuItem, TopBarUserMenuView } from "./TopBarUserMenuView"
import { UserMainViewController } from "./UserMain/UserMainViewController"
import { RHDetachedLoadingView } from "./Core/RHDetachedLoadingView"
import { UserProfileViewController } from "./UserProfileViewController"


// Offerer mode: orange palette derived from the Bootstrap amber/orange base.
// Procurer mode: Bootstrap blue sourced from BSColor.primary.
// The two accent colours are simply swapped between modes.
const _OFFERER_TOP_BAR_COLOR = RHColors.offererTopBar
const _OFFERER_BOTTOM_BAR_COLOR = RHColors.offererBottomBar
// Company label accent in procurer mode is orange; in offerer mode it is blue.
const _PROCURER_COMPANY_LABEL_COLOR = RHColors.warningAmber
const _OFFERER_COMPANY_LABEL_COLOR = RHColor.primary
// Hide only the Arved nav entry. Invoice routes/controllers stay available; flip to YES to restore the navbar item.
const _SHOW_INVOICES_NAVIGATION_BUTTON: boolean = NO


/**
 * Single authoritative list of all view controllers that have a navigation
 * button in the bottom bar. Used for the button group, the "select correct
 * button" logic, and the full nav-button array, so each VC appears exactly
 * once.
 */
const _ALL_NAV_VIEW_CONTROLLERS = [
    UserMainViewController,
    ProcurementSearchViewController,
    LogViewController,
    // ProcurementPlanViewController,
    NewProcurementViewController,
    NewProcurementPreviewViewController,
    MyOngoingProcurementsViewController,
    PublishedProcurementsViewController,
    IncomingOffersViewController,
    OutgoingOffersViewController,
    ContractsViewController,
    StatisticsMainViewController,
    // Extend the const array with the invoices VC so the button group and
    // route-selection logic cover it automatically.
    // (Since the array is `as const`, the cleanest approach is to add it
    //  directly to the array literal in the source.)
    InvoicesViewController,
    CompanyViewController
] as const


// Logo title initials ("H", "R") render larger than the rest to fake small-caps,
// since Roboto Condensed has no small-cap glyphs. The "Hangete Register" brand
// value in LanguageService wraps each word's first letter in <span class="rhLogoInitial">.
UIView.injectCSS(
    `.rhLogoInitial { font-size: ${RHNavbar.topBarTitleInitialFontSize}; font-weight: ${RHNavbar.topBarTitleInitialFontWeight}; }`,
    "rhLogoInitialStyle"
)


type RHTopBarModeButtonPosition = "standalone" | "leading" | "middle" | "trailing"


class RHTopBarModeButton extends RHBarButton {

    _position: RHTopBarModeButtonPosition


    constructor(position: RHTopBarModeButtonPosition) {

        super()

        this._position = position;

        (this as RHTopBarModeButton).configureWithObject({
            colors: {
                titleLabel: {
                    normal: RHNavbar.topBarModeToggle.normal.textColor,
                    hovered: RHNavbar.topBarModeToggle.hovered.textColor,
                    highlighted: RHNavbar.topBarModeToggle.highlighted.textColor,
                    focused: RHNavbar.topBarModeToggle.hovered.textColor,
                    selected: RHNavbar.topBarModeToggle.selected.textColor,
                    selectedAndHighlighted: RHNavbar.topBarModeToggle.selectedHighlighted.textColor
                },
                background: {
                    normal: RHNavbar.topBarModeToggle.normal.backgroundColor,
                    hovered: RHNavbar.topBarModeToggle.hovered.backgroundColor,
                    highlighted: RHNavbar.topBarModeToggle.highlighted.backgroundColor,
                    focused: RHNavbar.topBarModeToggle.hovered.backgroundColor,
                    selected: RHNavbar.topBarModeToggle.selected.backgroundColor,
                    selectedAndHighlighted: RHNavbar.topBarModeToggle.selectedHighlighted.backgroundColor
                }
            },
            style: {
                transition: RHNavbar.topBarModeToggle.transition
            }
        })
        this.updateSegmentPosition(position)
        this.updateContentForCurrentState()
    }


    updateSegmentPosition(position: RHTopBarModeButtonPosition) {

        this._position = position

        let borderRadius = "0"
        if (position === "standalone") {
            borderRadius = RHButtons.barBorderRadius
        }
        else if (position === "leading") {
            borderRadius = RHNavbar.topBarModeToggle.leadingSegmentBorderRadius
        }
        else if (position === "trailing") {
            borderRadius = RHNavbar.topBarModeToggle.trailingSegmentBorderRadius
        }

        (this as RHTopBarModeButton).configureWithObject({
            style: { borderRadius }
        })
        this._applyOuterBorder()
    }


    override updateContentForCurrentState() {

        super.updateContentForCurrentState()

        let boxShadow = RHNavbar.topBarModeToggle.normal.boxShadow
        if (this.selected && this.highlighted) {
            boxShadow = RHNavbar.topBarModeToggle.selectedHighlighted.boxShadow
        }
        else if (this.selected) {
            boxShadow = RHNavbar.topBarModeToggle.selected.boxShadow
        }
        else if (this.focused) {
            boxShadow = RHNavbar.topBarModeToggle.hovered.boxShadow
        }
        else if (this.highlighted) {
            boxShadow = RHNavbar.topBarModeToggle.highlighted.boxShadow
        }
        else if (this.hovered) {
            boxShadow = RHNavbar.topBarModeToggle.hovered.boxShadow
        }

        let outline = "none"
        if (this.focused) {
            outline = RHNavbar.topBarModeToggle.focusOutline
        }

        (this as RHTopBarModeButton).configureWithObject({
            style: {
                boxShadow,
                outline,
                outlineOffset: RHNavbar.topBarModeToggle.focusOutlineOffset
            }
        })
        this._applyOuterBorder()
    }


    _applyOuterBorder() {

        const outerBorderStyle = RHNavbar.topBarModeToggle.outerBorderStyle
        let borderLeft = "none"
        let borderRight = "none"

        if (this._position === "standalone") {
            borderLeft = outerBorderStyle
            borderRight = outerBorderStyle
        }
        else if (this._position === "leading") {
            borderLeft = outerBorderStyle
        }
        else if (this._position === "trailing") {
            borderRight = outerBorderStyle
        }

        (this as RHTopBarModeButton).configureWithObject({
            style: {
                border: "none",
                borderTop: outerBorderStyle,
                borderBottom: outerBorderStyle,
                borderLeft,
                borderRight
            }
        })
    }

}


export class TopBarView extends UIView {
    
    // Define containers first
    topSectionView = new UIView().configuredWithObject({
        backgroundColor: RHNavbar.topBarTopBarBackground
    }).addedAsSubviewToView(this)
    
    bottomBarView = new UIView().configuredWithObject({
        backgroundColor: RHNavbar.topBarBottomBarBackground
    }).addedAsSubviewToView(this)
    
    titleLabel = new UITextView("TopBarTitleLabel")
        .configuredWithObject({
            
            setText: CALL("topBarTitle", "TestPage"),
            textColor: RHNavbar.topBarTitleColor,
            fontSize: RHNavbar.topBarTitleFontSize,
            userInteractionEnabled: YES,
            nativeSelectionEnabled: NO,
            style: {
                cursor: "pointer",
                fontFamily: RHNavbar.topBarTitleFontFamily,
                letterSpacing: RHNavbar.topBarTitleLetterSpacing,
                textTransform: RHNavbar.topBarTitleTextTransform
            },
            controlEventTargetAccumulator: {
                PointerUpInside: (sender, event) => {
                    this._routeForBrandSelection().apply()
                }
            }
            
        }).addedAsSubviewToView(this.topSectionView)
    
    // User/company area: a single clickable button that opens the RH user menu.
    // The labels and the caret are its children; it lays them out itself.
    _userMenuButtonIsHovered = NO
    _userMenuButtonIsActive = NO

    userMenuButton = new UIView("TopBarUserMenuButton").configuredWithObject({
        userInteractionEnabled: YES,
        backgroundColor: new UIColor(RHNavbar.userMenuButtonIdleBackgroundColor),
        style: {
            cursor: "pointer",
            borderRadius: RHNavbar.userMenuButtonBorderRadius,
            lineHeight: `${RHNavbar.topBarUserInfoLabelHeight}px`,
            boxShadow: "none",
            transition: RHNavbar.userMenuButtonTransition
        },
        controlEventTargetAccumulator: {
            PointerHover: () => {
                this._userMenuButtonIsHovered = YES
                this._applyUserMenuButtonState()
            },
            PointerLeave: () => {
                this._userMenuButtonIsHovered = NO
                this._applyUserMenuButtonState()
            },
            PointerUpInside: () => {
                this._presentUserMenu()
            },
            EnterDown: (_sender, event) => {
                event.preventDefault()
                this._presentUserMenu()
            },
            SpaceDown: (_sender, event) => {
                event.preventDefault()
                this._presentUserMenu()
            }
        },
        layoutSubviews: EXTEND(() => this._layoutUserMenuButtonContents())
    }).addedAsSubviewToView(this.topSectionView)

    // User info labels (children of the clickable user button)
    userNameLabel = new UITextView("TopBarUserNameLabel")
        .configuredWithObject({
            textColor: RHNavbar.topBarUserNameColor,
            fontSize: RHNavbar.topBarUserNameFontSize,
            userInteractionEnabled: NO,
            nativeSelectionEnabled: NO,
            style: { fontFamily: RHNavbar.topBarUserNameFontFamily }
        }).addedAsSubviewToView(this.userMenuButton)

    readonly nameCompletionLink = new UILink("TopBarNameCompletionLink").configuredWithObject({
        text: 'Sisesta enda nimi "Minu konto" alt.',
        stopsPointerEventPropagation: YES,
        style: {
            color: RHNavbar.topBarUserNameColor.stringValue,
            fontFamily: RHNavbar.topBarUserNameFontFamily,
            fontSize: `${RHNavbar.topBarUserNameFontSize}pt`,
            textDecoration: "underline"
        },
        targetRouteForCurrentState: () => UIRoute.currentRoute
            .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
            .routeWithViewControllerComponent(SettingsViewController, { tab: "profile" })
            .routeWithViewControllerComponent(UserProfileViewController, { focusName: "YES" }),
        controlEventTargetAccumulator: {
            EnterDown: (_sender, event) => event.stopPropagation(),
            SpaceDown: (_sender, event) => event.stopPropagation()
        }
    })

    readonly userRoleLabel = new UITextView("TopBarUserRoleLabel")
        .configuredWithObject({
            textColor: RHNavbar.topBarUserNameColor,
            fontSize: RHNavbar.topBarUserNameFontSize,
            userInteractionEnabled: NO,
            nativeSelectionEnabled: NO,
            style: { fontFamily: RHNavbar.topBarUserNameFontFamily }
        }).addedAsSubviewToView(this.userMenuButton)

    userCompanyLabel = new UITextView("TopBarUserCompanyLabel")
        .configuredWithObject({
            textColor: RHNavbar.topBarCompanyLabelColor,
            fontSize: RHNavbar.topBarUserNameFontSize,
            style: {
                fontFamily: RHNavbar.topBarCompanyLabelFontFamily,
                fontWeight: RHFont.weightBold
            },
            userInteractionEnabled: NO,
            nativeSelectionEnabled: NO
        }).addedAsSubviewToView(this.userMenuButton)

    // Subtle caret hinting that the user button opens a menu
    userMenuChevronLabel = new UITextView("TopBarUserMenuChevron")
        .configuredWithObject({
            text: "\u25BE",
            userInteractionEnabled: NO,
            nativeSelectionEnabled: NO,
            backgroundColor: UIColor.transparentColor,
            textColor: new UIColor(RHNavbar.userMenuChevronColor),
            textAlignment: UITextView.textAlignment.center,
            style: { fontSize: RHNavbar.userMenuChevronFontSize }
        }).addedAsSubviewToView(this.userMenuButton)

    readonly _userMenu = new TopBarUserMenuView("TopBarUserMenu")

    // Role-mode segments. Membership in the view tree is derived from the
    // current company's independent procurer / offerer / auditor role flags.
    _procurerModeButton = new RHTopBarModeButton("leading").configuredWithObject({
        titleLabel: { text: "Hankija" },
        isToggleable: YES,
        altModeKey: "H",
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: (sender) => {
                if ((sender as RHBarButton).selected) {
                    return
                }
            }
        }
    })
    
    _offererModeButton = new RHTopBarModeButton("trailing").configuredWithObject({
        titleLabel: { text: "Pakkuja" },
        isToggleable: YES,
        altModeKey: "K",
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: (sender) => {
                if ((sender as RHBarButton).selected) {
                    return
                }
            }
        }
    })

    _auditorModeButton = new RHTopBarModeButton("trailing").configuredWithObject({
        titleLabel: { text: "Audiitor" },
        isToggleable: YES,
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: (sender) => {
                if ((sender as RHBarButton).selected) {
                    return
                }
            }
        }
    })

    _modeButtonsByApplicationMode = {
        procurer: this._procurerModeButton,
        offerer: this._offererModeButton,
        auditor: this._auditorModeButton
    }

    _modeButtonGroup = new CBButtonGroup(this._modeButtonsByApplicationMode).configuredWithObject({
        selectionDidChange: (_selectedButton, value) => {
            this._applyApplicationModeSelection(value).then(nil)
        }
    })
    
    // All nav buttons; none are pre-added to bottomBarView. _updateVisibleNavigationButtons manages membership.
    userMainButton = new RHBarLinkButton().configuredWithObject({
        titleLabel: { text: "Avaleht" },
        altModeKey: "A"
    })

    procurerMainButton = new RHBarLinkButton().configuredWithObject({
        titleLabel: { text: "Töölaud" },
        altModeKey: "A"
    })

    searchButton = new RHBarLinkButton().configuredWithObject({
        titleLabel: { text: "Otsing" },
        altModeKey: "O"
    })
    
    eventsButton = new RHBarLinkButton().configuredWithObject({
        titleLabel: { text: "Teavitused" },
        altModeKey: "E"
    })
    
    procurementPlanButton = new RHBarLinkButton().configuredWithObject({
        titleLabel: { text: "Hankeplaan" }
    })
    
    newProcurementButton = new RHBarLinkButton().configuredWithObject({
        titleLabel: { text: "Uus hange" },
        altModeKey: "U"
    })
    
    ongoingProcurementsButton = new RHBarLinkButton().configuredWithObject({
        titleLabel: { text: "Töös hanked" },
        altModeKey: "M"
    })
    
    publishedProcurementsButton = new RHBarLinkButton().configuredWithObject({
        titleLabel: {
            text: labelForRHViewport(RHResponsiveTextTokens.topBarLabels.publishedProcurements),
            hoverText: RHResponsiveTextTokens.topBarLabels.publishedProcurements.regular
        },
        altModeKey: "V"
    })
    
    incomingOffersButton = new RHBarLinkButton().configuredWithObject({
        titleLabel: {
            text: labelForRHViewport(RHResponsiveTextTokens.topBarLabels.incomingOffers),
            hoverText: RHResponsiveTextTokens.topBarLabels.incomingOffers.regular
        },
        altModeKey: "P"
    })
    
    outgoingOffersButton = new RHBarLinkButton().configuredWithObject({
        titleLabel: {
            text: labelForRHViewport(RHResponsiveTextTokens.topBarLabels.outgoingOffers),
            hoverText: RHResponsiveTextTokens.topBarLabels.outgoingOffers.regular
        },
        altModeKey: "I"
    })
    
    dealsButton = new RHBarLinkButton().configuredWithObject({
        titleLabel: { text: "Lepingud" },
        altModeKey: "T"
    })
    
    invoicesButton = new RHBarLinkButton().configuredWithObject({
        titleLabel: { text: "Arved" },
        altModeKey: "F"
    })
    
    statisticsButton = new RHBarLinkButton().configuredWithObject({
        titleLabel: {
            text: labelForRHViewport(RHResponsiveTextTokens.topBarLabels.statistics),
            hoverText: RHResponsiveTextTokens.topBarLabels.statistics.regular
        },
        altModeKey: "R"
    })
    
    companyButton = new RHBarLinkButton().configuredWithObject({
        titleLabel: { text: "Ettevõte" },
        altModeKey: "D"
    })
    
    feedbackButton = new RHBarLinkButton().configuredWithObject({
        titleLabel: { text: "Anna tagasisidet" },
        variant: "callToAction",
        altModeKey: "G"
    }).addedAsSubviewToView(this.bottomBarView)
    
    // Navigation button group for selection state only; targetRouteForCurrentState handles routing.
    navigationButtonGroup = new CBButtonGroup(
        Object.fromEntries(
            _ALL_NAV_VIEW_CONTROLLERS
                .filter(vc => vc !== NewProcurementPreviewViewController)
                .map(vc => [vc.routeComponentName, this._navButtonForViewController(vc)])
                // NewProcurementPreviewViewController shares the newProcurementButton
                .concat([[NewProcurementPreviewViewController.routeComponentName, this.newProcurementButton]])
        )
    )
    
    loginButton = new RHBarLinkButton().configuredWithObject({
        titleLabel: { text: "Logi sisse" },
        variant: "outlineOnDarkBackground",
        altModeKey: "L"
    })
    
    // Tracks which log button is currently in the view tree. Only the login button is
    // ever shown here now; logged-in users get the user menu instead, and logout moved
    // into that menu.
    _currentLogButton?: RHBarLinkButton | RHBarButton
    
    _visibleNavigationButtons: RHBarLinkButton[] = []

    _visibleApplicationModes: RHApplicationMode[] = []

    _visibleModeButtons: RHTopBarModeButton[] = []
    
    // Whether the mode toggle is currently shown
    _showsModeToggle = NO
    
    /**
     * Cached company affiliation, stored so that mode changes can recompute
     * visible buttons without a second network round-trip.
     */
    _cachedCurrentCompanyAffiliation?: Awaited<typeof RHCore.sharedInstance.currentCompanyAffiliation>
    _targetUpdateGeneration = 0
    
    override _focusManager = new RHFocusManager(() => {
        const items: UIView[] = []
        if (this._showsModeToggle) {
            items.push(...this._visibleModeButtons)
        }
        if (this._currentLogButton) {
            items.push(this._currentLogButton)
        }
        if (IS(this.userMenuButton.superview)) {
            items.push(this.userMenuButton)
            if (this.userMenuButton.hasSubview(this.nameCompletionLink)) {
                items.push(this.nameCompletionLink)
            }
        }
        items.push(...this._visibleNavigationButtons)
        items.push(this.feedbackButton)
        return items
    })
    
    
    constructor(elementID: string | undefined, element?: HTMLElement & LooseObject) {
        
        super(elementID, element)
        
        this.addStyleClass("TopBarView")

        this.userMenuButton.viewHTMLElement.setAttribute("role", "button")
        this.userMenuButton.viewHTMLElement.setAttribute("aria-label", "Kasutaja menüü")
        this.userMenuButton.viewHTMLElement.setAttribute("aria-haspopup", "menu")
        this.userMenuButton.viewHTMLElement.setAttribute("aria-expanded", "false")
        this.userMenuButton.viewHTMLElement.tabIndex = 0
        
        ;(this as TopBarView).configureWithObject({
            setBorder: [nil, 0, CBColor.primaryContentColor],
            style: {
                borderBottomWidth: "0px",
                fontSize: "15pt",
                zIndex: "10",
                boxShadow: RHNavbar.topBarShadow
            }
        })
        
        this._updateTopSectionBackgroundForCurrentApplicationMode()
        this._updateTargetRoutesAndButtons()
        this._wireNavButtonResetInterceptors()

    }


    // Keep the current route untouched so unfinished forms remain open.
    _openHelpInNewWindow() {
        const helpRoute = UIRoute.currentRoute
            .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
            .routeWithViewControllerComponent(HelpViewController, nil)
        const baseUrl = ("" + window.location.href).split("#")[0]
        window.open(baseUrl + helpRoute.linkRepresentation, "_blank", "noopener")
    }


    _applyUserMenuButtonState() {
        let backgroundColor = new UIColor(RHNavbar.userMenuButtonIdleBackgroundColor)
        if (this._userMenuButtonIsHovered) {
            backgroundColor = new UIColor(RHNavbar.userMenuButtonHoverBackgroundColor)
        }
        let boxShadow = "none"
        if (this._userMenuButtonIsActive) {
            boxShadow = `inset 0 0 0 ${RHNavbar.userMenuButtonActiveRingWidth}px ` +
                RHNavbar.userMenuButtonActiveRingColor.stringValue
        }
        this.userMenuButton.configureWithObject({
            backgroundColor,
            style: { boxShadow }
        })
    }
    
    
    _wireNavButtonResetInterceptors() {
        
        const navButtons: RHBarLinkButton[] = [
            this.userMainButton,
            this.procurerMainButton,
            this.searchButton,
            this.eventsButton,
            this.procurementPlanButton,
            this.newProcurementButton,
            this.ongoingProcurementsButton,
            this.publishedProcurementsButton,
            this.incomingOffersButton,
            this.outgoingOffersButton,
            this.dealsButton,
            this.statisticsButton,
            this.companyButton
        ]
        
        navButtons.push(this.invoicesButton)
        
        for (const button of navButtons) {
            button.primaryActionWasTriggered = (_sender, event) => {
                if (IS(event)) {
                    event.preventDefault()
                }
                this._handleNavButtonTapped(button).then(nil)
            }
        }
        
    }
    
    
    async _handleNavButtonTapped(button: RHBarLinkButton) {
        const targetRoute = button.targetRouteForCurrentState?.()
        if (!targetRoute) {
            return
        }
        
        if (typeof targetRoute === "string") {
            window.location.hash = targetRoute
        }
        else {
            // When the button is already selected the route won't change, so we
            // must force the navigation to re-run handleRoute and reset the VC.
            targetRoute.apply(button.selected)
        }
        
    }
    
    
    async _applyApplicationModeSelection(applicationMode: string | undefined) {
        if (applicationMode !== "procurer" && applicationMode !== "offerer" && applicationMode !== "auditor") {
            return
        }
        if (!this._visibleApplicationModes.includes(applicationMode)) {
            this._restoreApplicationModeSelection()
            return
        }

        const routeWasApplied = await this._routeForApplicationModeSelection(applicationMode)
            .applyByReplacingCurrentRouteInHistory()

        if (!routeWasApplied) {
            this._restoreApplicationModeSelection()
        }
    }


    _routeForApplicationModeSelection(applicationMode: RHApplicationMode): UIRoute {
        const routeWithSelectedMode = UIRoute.currentRoute
            .routeBySettingParameterInComponent("settings", "mode", applicationMode)

        if (
            IS(UIRoute.currentRoute.componentWithViewController(ContractsViewController)) &&
            applicationMode !== "auditor"
        ) {
            return routeWithSelectedMode
        }
        if (this._currentNavigationButtonWillRemainVisibleForApplicationMode(applicationMode)) {
            return routeWithSelectedMode
        }

        if (applicationMode === "auditor") {
            return routeWithSelectedMode
                .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
                .routeWithViewControllerComponent(ProcurementSearchViewController, nil)
        }

        return routeWithSelectedMode
            .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
            .routeWithViewControllerComponent(UserMainViewController, nil)
    }


    _currentNavigationButtonWillRemainVisibleForApplicationMode(
        applicationMode: RHApplicationMode
    ): boolean {
        const currentButton = this._navigationButtonForCurrentRoute()
        if (!currentButton) {
            return YES
        }

        return this._visibleNavigationButtonsForAffiliationInApplicationMode(
            this._cachedCurrentCompanyAffiliation,
            applicationMode
        ).includes(currentButton)
    }


    _navigationButtonForCurrentRoute(): RHBarLinkButton | undefined {
        const route = UIRoute.currentRoute

        if (this._isUnaffiliatedCompanyAffiliationRoute(route)) {
            return this.userMainButton
        }

        if (
            IS(route.componentWithViewController(UserMainViewController)) &&
            RHCore.sharedInstance.isInOffererMode &&
            IS(this._cachedCurrentCompanyAffiliation) &&
            IS(this.publishedProcurementsButton.superview)
        ) {
            return this.publishedProcurementsButton
        }

        const viewControllerClass = _ALL_NAV_VIEW_CONTROLLERS.find(
            vc => IS(route.componentWithViewController(vc))
        )
        if (!viewControllerClass) {
            return undefined
        }

        return this._navButtonForViewController(viewControllerClass)
    }


    _isUnaffiliatedCompanyAffiliationRoute(route: UIRoute): boolean {
        if (IS(this._cachedCurrentCompanyAffiliation)) {
            return NO
        }

        const companyRouteComponent = route.componentWithViewController(CompanyViewController)
        if (IS_NOT(companyRouteComponent)) {
            return NO
        }

        return CompanyViewController.tabInRoute(route) === "affiliation"
    }


    _restoreApplicationModeSelection() {
        const currentMode = RHCore.sharedInstance.applicationMode
        if (this._visibleApplicationModes.includes(currentMode)) {
            this._modeButtonGroup.value = currentMode
            return
        }

        this._modeButtonGroup.buttonsMap.allValues.everyElement.selected = NO
    }


    private _updateTopSectionBackgroundForCurrentApplicationMode() {
        const isInOffererMode = RHCore.sharedInstance.isInOffererMode && RHCore.sharedInstance.isUserLoggedIn
        if (isInOffererMode) {
            this.topSectionView.backgroundColor = RHNavbar.topBarTopBarBackground
            this.titleLabel.textColor = RHNavbar.topBarTitleColor
            this.userNameLabel.textColor = RHNavbar.topBarUserNameColor
            this.userCompanyLabel.textColor = RHNavbar.topBarCompanyLabelColor
            this.bottomBarView.backgroundColor = RHNavbar.topBarBottomBarBackground
            this._applyLightColorsToLoginButton()
        }
        else {
            this.topSectionView.backgroundColor = RHNavbar.topBarTopBarBackground
            this.titleLabel.textColor = RHNavbar.topBarTitleColor
            this.userNameLabel.textColor = RHNavbar.topBarUserNameColor
            this.userCompanyLabel.textColor = RHNavbar.topBarCompanyLabelColor
            this.bottomBarView.backgroundColor = RHNavbar.topBarBottomBarBackground
            this._applyLightColorsToLoginButton()
        }
    }
    
    _applyLightColorsToLoginButton() {
        this.loginButton.configureWithObject({
            colors: {
                ...this.loginButton.colors,
                titleLabel: {
                    ...this.loginButton.colors.titleLabel,
                    normal: UIColor.whiteColor,
                    highlighted: UIColor.whiteColor.colorWithAlpha(0.6),
                    selected: UIColor.whiteColor
                }
            }
        })
        this.loginButton.updateContentForCurrentState()
    }
    
    
    async _updateTargetRoutesAndButtons() {
        const updateGeneration = ++this._targetUpdateGeneration
        
        this.userMainButton.targetRouteForCurrentState = () =>
            UIRoute.currentRoute
                .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
                .routeWithViewControllerComponent(UserMainViewController, nil)

        this.procurerMainButton.targetRouteForCurrentState = () =>
            this._routeForPublishedProcurementsInProcurerMode()

        this.searchButton.targetRouteForCurrentState = () =>
            UIRoute.currentRoute
                .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
                .routeWithViewControllerComponent(ProcurementSearchViewController, nil)

        this.eventsButton.targetRouteForCurrentState = () =>
            UIRoute.currentRoute
                .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
                .routeWithViewControllerComponent(LogViewController, nil)
        
        this.procurementPlanButton.targetRouteForCurrentState = () =>
            UIRoute.currentRoute
                .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
                .routeWithViewControllerComponent(ProcurementPlanViewController, nil)
        
        this.newProcurementButton.targetRouteForCurrentState = () =>
            UIRoute.currentRoute
                .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
                .routeWithViewControllerComponent(NewProcurementViewController, nil)
        
        this.ongoingProcurementsButton.targetRouteForCurrentState = () =>
            UIRoute.currentRoute
                .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
                .routeWithViewControllerComponent(MyOngoingProcurementsViewController, nil)
        
        this.publishedProcurementsButton.targetRouteForCurrentState = () => {
            const baseRoute = UIRoute.currentRoute
                .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
            if (RHCore.sharedInstance.isInOffererMode && IS(this._cachedCurrentCompanyAffiliation)) {
                return baseRoute.routeWithViewControllerComponent(UserMainViewController, nil)
            }
            return baseRoute.routeWithViewControllerComponent(PublishedProcurementsViewController, nil)
        }
        
        this.incomingOffersButton.targetRouteForCurrentState = () =>
            UIRoute.currentRoute
                .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
                .routeWithViewControllerComponent(IncomingOffersViewController, nil)
        
        this.outgoingOffersButton.targetRouteForCurrentState = () =>
            UIRoute.currentRoute
                .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
                .routeWithViewControllerComponent(OutgoingOffersViewController, nil)
        
        this.dealsButton.targetRouteForCurrentState = () =>
            UIRoute.currentRoute
                .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
                .routeWithViewControllerComponent(ContractsViewController, nil)
        
        this.invoicesButton.targetRouteForCurrentState = () =>
            UIRoute.currentRoute
                .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
                .routeWithViewControllerComponent(InvoicesViewController, nil)
        
        this.statisticsButton.targetRouteForCurrentState = () =>
            UIRoute.currentRoute
                .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
                .routeWithViewControllerComponent(StatisticsMainViewController, nil)
        
        this.companyButton.targetRouteForCurrentState = () =>
            UIRoute.currentRoute
                .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
                .routeWithViewControllerComponent(CompanyViewController, nil)
        
        this.loginButton.targetRouteForCurrentState = () =>
            UIRoute.currentRoute
                .routeByRemovingComponentsOtherThanOnesNamed([
                    "settings", ProcurementDetailsViewController.routeComponentName
                ])
                .routeWithViewControllerComponent(LoginViewController, nil)
        
        this.feedbackButton.targetRouteForCurrentState = () =>
            UIRoute.currentRoute
                .routeWithViewControllerComponent(FeedbackViewController, nil)
        
        let affiliation = await RHCore.sharedInstance.currentCompanyAffiliation
        if (updateGeneration !== this._targetUpdateGeneration) {
            return
        }
        if (IS(affiliation) && IS_NOT(affiliation.company)) {
            affiliation = undefined
        }
        
        this._cachedCurrentCompanyAffiliation = affiliation
        this._updateUserLabels()
        this._updateVisibleNavigationButtons(affiliation)
        this._updateModeToggleButtonsDisplay(affiliation)
        this.selectCorrectButtons()
        
    }


    _routeForBrandSelection(): UIRoute {
        const baseRoute = UIRoute.currentRoute
            .routeByRemovingComponentsOtherThanOnesNamed(["settings"])

        if (!RHCore.sharedInstance.isUserLoggedIn) {
            return baseRoute.routeWithViewControllerComponent(LoginViewController, nil)
        }

        if (RHCore.sharedInstance.applicationMode === "procurer") {
            return this._routeForPublishedProcurementsInProcurerMode()
        }

        if (RHCore.sharedInstance.applicationMode === "auditor") {
            return baseRoute.routeWithViewControllerComponent(ProcurementSearchViewController, nil)
        }

        return baseRoute.routeWithViewControllerComponent(UserMainViewController, nil)
    }


    _routeForPublishedProcurementsInProcurerMode(): UIRoute {
        return UIRoute.currentRoute
            .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
            .routeWithViewControllerComponent(
                UserMainViewController,
                { [UserMainViewController.ParameterIdentifierName.tab]: "published" }
            )
    }
    
    
    _updateVisibleNavigationButtons(affiliation: Awaited<typeof RHCore.sharedInstance.currentCompanyAffiliation>) {

        if (IS(affiliation)) {
            this.userMainButton.configureWithObject({
                titleLabel: { text: "Avaleht" },
                altModeKey: "A"
            })
        }
        else {
            this.userMainButton.configureWithObject({
                titleLabel: { text: "Seo ettevõte" },
                altModeKey: "S"
            })
        }
        
        const allNavButtons: RHBarLinkButton[] = [
            this.userMainButton,
            this.procurerMainButton,
            this.searchButton,
            this.eventsButton,
            this.procurementPlanButton,
            this.newProcurementButton,
            this.ongoingProcurementsButton,
            this.publishedProcurementsButton,
            this.incomingOffersButton,
            this.outgoingOffersButton,
            this.dealsButton,
            this.invoicesButton,
            this.statisticsButton,
            this.companyButton
        ]
        allNavButtons.everyElement.removeFromSuperview()
        
        const nextButtons = this._visibleNavigationButtonsForAffiliationInApplicationMode(
            affiliation,
            RHCore.sharedInstance.applicationMode
        )
        nextButtons.everyElement.addedAsSubviewToView(this.bottomBarView)
        this._visibleNavigationButtons = nextButtons
        
    }
    
    
    _visibleNavigationButtonsForAffiliationInApplicationMode(
        affiliation: Awaited<typeof RHCore.sharedInstance.currentCompanyAffiliation>,
        applicationMode: RHApplicationMode
    ): RHBarLinkButton[] {
        // A mailed temporary password blocks every socket message except the
        // password change itself (see SocketSession.keysAllowedWhilePasswordChangeIsRequired) -
        // showing navigation that only bounces back to the profile screen is
        // just a confusing decoy, so show none of it.
        if (RHCore.sharedInstance.userProfile?.isPasswordChangeRequired) {
            return []
        }

        const isOfferer = affiliation?.isOfferer == YES
        const isAuditor = affiliation?.isAuditor == YES
        const hasCompanyAffiliation = IS(affiliation?.company)

        const canActAsProcurer = RHCore.userCanActAsProcurerThroughAffiliation(affiliation)

        const isInProcurerMode = applicationMode === "procurer"
        const isInOffererMode = applicationMode === "offerer"
        const isInAuditorMode = applicationMode === "auditor"

        const buttons: RHBarLinkButton[] = []

        if (!hasCompanyAffiliation) {
            // Affiliation-less users can finish registration or browse public procurements.
            buttons.push(this.userMainButton)
            buttons.push(this.publishedProcurementsButton)
        }
        else if (isInAuditorMode && isAuditor) {
            buttons.push(this.searchButton)
        }
        else if (isInProcurerMode && canActAsProcurer) {
            buttons.push(this.procurerMainButton)
            buttons.push(this.searchButton)
        }
        else if (isInOffererMode && isOfferer) {
            // Offerer navbar keeps the primary "Töölaud" entry plus public search.
            // "Esitatud pakkumused" and
            // "Lepingud" is outside the bar; the offerer reaches their offer from the
            // procurement's "Minu pakkumus" tab (incl. the "Esitatud"
            // status link) and contracts from the procurement's "Lepingud" tab.
            // The views stay in code, reachable by route. eventsButton +
            // statisticsButton (below) + the persistent feedbackButton complete it.
            buttons.push(this.publishedProcurementsButton)
            buttons.push(this.searchButton)
        }
        else {
            buttons.push(this.userMainButton)
        }

        if (_SHOW_INVOICES_NAVIGATION_BUTTON == YES && hasCompanyAffiliation) {
            buttons.push(this.invoicesButton)
        }

        buttons.push(this.eventsButton)
        buttons.push(this.statisticsButton)

        // Company access lives in the user menu. companyButton stays declared only
        // for route-selection mapping; it is never added to the bar.


        return buttons
    }
    
    
    _updateModeToggleButtonsDisplay(affiliation: Awaited<typeof RHCore.sharedInstance.currentCompanyAffiliation>) {
        
        const visibleApplicationModes = this._applicationModesForAffiliation(affiliation)
        const visibleModeButtons = visibleApplicationModes.map(
            applicationMode => this._modeButtonsByApplicationMode[applicationMode]
        )
        const currentMode = RHCore.sharedInstance.applicationMode

        this._modeButtonGroup.buttonsMap.allValues.everyElement.removeFromSuperview()
        visibleModeButtons.everyElement.addedAsSubviewToView(this.topSectionView)

        this._visibleApplicationModes = visibleApplicationModes
        this._visibleModeButtons = visibleModeButtons
        this._showsModeToggle = visibleModeButtons.length > 0

        this._updateModeButtonPositions(visibleModeButtons)

        if (visibleApplicationModes.length > 0) {
            let selectedMode = currentMode
            if (!visibleApplicationModes.includes(selectedMode)) {
                selectedMode = visibleApplicationModes.firstElement
                RHCore.sharedInstance.applicationMode = selectedMode
            }
            this._modeButtonGroup.value = selectedMode
        }
        else {
            this._modeButtonGroup.buttonsMap.allValues.everyElement.selected = NO
        }
        
        this.setNeedsLayout()
        
    }


    _applicationModesForAffiliation(
        affiliation: Awaited<typeof RHCore.sharedInstance.currentCompanyAffiliation>
    ): RHApplicationMode[] {
        const applicationModes: RHApplicationMode[] = []

        if (IS_NOT(affiliation?.company)) {
            return applicationModes
        }
        if (RHCore.userCanActAsProcurerThroughAffiliation(affiliation)) {
            applicationModes.push("procurer")
        }
        if (affiliation?.isOfferer == YES) {
            applicationModes.push("offerer")
        }
        if (affiliation?.isAuditor == YES) {
            applicationModes.push("auditor")
        }

        return applicationModes
    }


    _updateModeButtonPositions(modeButtons: RHTopBarModeButton[]) {
        modeButtons.forEach((modeButton, index) => {
            let position: RHTopBarModeButtonPosition = "middle"
            if (modeButtons.length === 1) {
                position = "standalone"
            }
            else if (index === 0) {
                position = "leading"
            }
            else if (index === modeButtons.length - 1) {
                position = "trailing"
            }
            modeButton.updateSegmentPosition(position)
        })
    }
    
    
    private updateLogButton() {
        const isLoggedIn = RHCore.sharedInstance.isUserLoggedIn
        const isOnLoginView = IS(UIRoute.currentRoute.componentWithViewController(LoginViewController))

        // Logged-in users get the clickable user/company menu button (logout lives inside
        // that menu). Logged-out users get the login button, hidden while on the login view.
        if (isLoggedIn) {
            this._currentLogButton?.removeFromSuperview()
            this._currentLogButton = undefined
            if (IS_NOT(this.userMenuButton.superview)) {
                this.userMenuButton.addedAsSubviewToView(this.topSectionView)
            }
        }
        else {
            if (IS(this.userMenuButton.superview)) {
                this.userMenuButton.removeFromSuperview()
            }
            this._userMenu.dismiss()

            const shouldShowLogin = !isOnLoginView
            if (shouldShowLogin) {
                if (this._currentLogButton !== this.loginButton || IS_NOT(this.loginButton.superview)) {
                    this._currentLogButton?.removeFromSuperview()
                    this._currentLogButton = this.loginButton
                    this.loginButton.addedAsSubviewToView(this.topSectionView)
                }
            }
            else {
                this._currentLogButton?.removeFromSuperview()
                this._currentLogButton = undefined
            }
        }

        this.setNeedsLayout()
    }


    async _presentUserMenu() {
        if (this._userMenu.isPresented) {
            this._userMenu.dismiss()
            return
        }
        await RHCore.sharedInstance.companyAffiliationStore.retrieveMyCompanyAffiliations()
        this._userMenu.presentFromTriggerView(this.userMenuButton, this._userMenuItems())
    }


    private _userMenuItems(): TopBarUserMenuItem[] {
        // Same forced-password-change lockdown as the bottom nav bar - only
        // logout is reachable until the mailed temporary password is replaced.
        if (RHCore.sharedInstance.userProfile?.isPasswordChangeRequired) {
            return [{
                title: "Logi välja",
                action: () => this._performLogout()
            }]
        }

        const items: TopBarUserMenuItem[] = []
        const isOfferer = this._cachedCurrentCompanyAffiliation?.isOfferer === YES
        const isInOffererMode = RHCore.sharedInstance.isInOffererMode
        const companyAffiliationStore = RHCore.sharedInstance.companyAffiliationStore
        const directCompanyAffiliations = companyAffiliationStore.affiliations.filter(affiliation =>
            affiliation.source !== "relationship" &&
            affiliation.membershipStatus === "active"
        )

        if (directCompanyAffiliations.length > 1) {
            for (const affiliation of directCompanyAffiliations) {
                const companyId = affiliation.company?._id?.toString()
                if (!companyId) {
                    continue
                }
                const companyLabel = affiliation.company?.name ?? companyId
                items.push({
                    title: companyLabel,
                    active: companyId === companyAffiliationStore.activeCompanyId,
                    action: () => this._activateCompanyWithIdFromUserMenu(companyId)
                })
            }
        }

        items.push({
            title: "Seaded",
            action: () => this._routeToSettings()
        })
        if (
            (isInOffererMode && isOfferer) ||
            (RHCore.sharedInstance.applicationMode === "procurer" && RHCore.userCanActAsProcurerThroughAffiliation(this._cachedCurrentCompanyAffiliation))
        ) {
            items.push({
                title: "Minu kõik lepingud",
                active: IS(UIRoute.currentRoute.componentWithViewController(ContractsViewController)),
                action: () => this._handleNavButtonTapped(this.dealsButton)
            })
        }
        if (isInOffererMode && isOfferer) {
            items.push({
                title: "Leitavus ja hanketeavitused",
                action: () => this._routeToCompanyTab("cpvInterests")
            })
        }

        items.push({
            title: "Abi",
            action: () => this._openHelpInNewWindow()
        })

        items.push({
            title: "Logi välja",
            action: () => this._performLogout()
        })

        return items
    }


    _routeToSettings() {
        UIRoute.currentRoute
            .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
            .routeWithViewControllerComponent(SettingsViewController, nil)
            .apply()
    }


    _activateCompanyWithIdFromUserMenu(companyId: string) {
        const companyAffiliationStore = RHCore.sharedInstance.companyAffiliationStore
        if (companyId === companyAffiliationStore.activeCompanyId) {
            return
        }

        companyAffiliationStore.deactivateDefaultAllCompaniesScope()
        if (!companyAffiliationStore.activateCompany(companyId)) {
            return
        }

        let targetRoute = UIRoute.currentRoute
            .routeBySettingParameterInComponent("settings", "mode", RHCore.sharedInstance.preferredApplicationMode)
        if (targetRoute.componentWithViewController(CompanyViewController)) {
            targetRoute = targetRoute.routeWithComponent(
                CompanyViewController.routeComponentName,
                {
                    [CompanyViewController.ParameterIdentifierName.tab]:
                        CompanyViewController.tabInRoute(targetRoute)
                }
            )
        }
        targetRoute.applyByReplacingCurrentRouteInHistoryWithoutRouteChangeApproval()
    }


    private _routeToCompanyTab(tab: string) {
        UIRoute.currentRoute
            .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
            .routeWithComponent(
                CompanyViewController.routeComponentName,
                { [CompanyViewController.ParameterIdentifierName.tab]: tab }
            )
            .apply()
    }


    private _performLogout() {
        this._coverPageBeforeLogout()
        window.location.href = "/auth/logout"
    }


    private _layoutUserMenuButtonContents() {
        const labelToChevronGap = RHNavbar.userMenuButtonLabelToChevronGap
        const chevronWidth = RHNavbar.userMenuChevronWidth
        const bounds = this.userMenuButton.contentBoundsWithInsets(
            RHNavbar.userMenuButtonHorizontalInset,
            RHNavbar.userMenuButtonHorizontalInset,
            RHSpacing.tiny,
            RHSpacing.tiny
        )

        bounds
            .rectangleWithWidth(chevronWidth, 1)
            .assignedAsFrameOfView(this.userMenuChevronLabel)

        bounds
            .rectangleWithInsets(0, chevronWidth + labelToChevronGap, 0, 0)
            .rectangleWithHeight(RHNavbar.topBarUserInfoBlockHeight, 0.5)
            .distributeViewsAlongHeight(
                [
                    IF(this.userMenuButton.hasSubview(this.nameCompletionLink))(() =>
                        this.nameCompletionLink
                    ).ELSE(() => this.userNameLabel),
                    this.userRoleLabel,
                    this.userCompanyLabel
                ],
                1,
                0,
                RHNavbar.topBarUserInfoLabelHeight
            )
    }
    
    
    _updateUserLabels() {
        const userProfile = RHCore.sharedInstance.userProfile
        const isLoggedIn = RHCore.sharedInstance.isUserLoggedIn
        const companyAffiliationStore = RHCore.sharedInstance.companyAffiliationStore
        const activeCompanyAffiliation = companyAffiliationStore.activeCompanyAffiliation
        
        if (isLoggedIn && userProfile) {
            const name = userProfile.contactInformation?.name?.trim() ?? ""
            if (!name || name.toLowerCase() === userProfile.email.toLowerCase()) {
                this.userNameLabel.text = RHTextSanitizer.escapedHTML(this.nameCompletionLink.text)
                this.userNameLabel.removeFromSuperview()
                this.nameCompletionLink.addedAsSubviewToView(this.userMenuButton)
            }
            else {
                this.nameCompletionLink.removeFromSuperview()
                this.userNameLabel.text = `Kasutaja: ${RHTextSanitizer.escapedHTML(name)}`
                this.userNameLabel.addedAsSubviewToView(this.userMenuButton)
            }

            const companyAffiliation = activeCompanyAffiliation ?? this._cachedCurrentCompanyAffiliation
            const roles = companyAffiliation?.roles ?? []
            let roleText = CompanyRoleBadgesView.displayLabelsForRoles(
                IF(roles.includes("administrator"))(() => ["administrator"]).ELSE(() =>
                    roles.filter(role => role === RHCore.sharedInstance.applicationMode)
                )
            ).join(", ")
            if (companyAffiliation?.source === "relationship") {
                roleText = "Emaettevõtte kaudu"
            }
            else if (!companyAffiliation && companyAffiliationStore.affiliationLoadState === "failed") {
                roleText = "Rolli laadimine ebaõnnestus"
            }
            this.userRoleLabel.text = `Roll: ${RHTextSanitizer.escapedHTML(roleText)}`
            this.userMenuButton.hoverText = this.userRoleLabel.text

            const companyName = activeCompanyAffiliation?.company?.name ??
                this._cachedCurrentCompanyAffiliation?.company?.name
            if (companyName) {
                if (activeCompanyAffiliation?.managingCompany?.name) {
                    this.userCompanyLabel.setText(
                        "topBarManagedCompany",
                        "%COMPANY_NAME% (haldajaks %MANAGING_COMPANY_NAME%)",
                        {
                            COMPANY_NAME: RHTextSanitizer.escapedHTML(companyName),
                            MANAGING_COMPANY_NAME: RHTextSanitizer.escapedHTML(activeCompanyAffiliation.managingCompany.name)
                        }
                    )
                }
                else {
                    this.userCompanyLabel.text = RHTextSanitizer.escapedHTML(companyName)
                }
            }
            else {
                if (companyAffiliationStore.affiliationLoadState === "failed") {
                    this.userCompanyLabel.text = "Ettevõtte seose laadimine ebaõnnestus"
                }
                else {
                    this.userCompanyLabel.text = "Kasutaja ei ole ühegi ettevõttega seotud"
                }
            }
            this.userCompanyLabel.textColor = RHNavbar.topBarCompanyLabelColor

            const companyLabelFont =
                `${RHFont.weightBold} ${RHNavbar.topBarUserNameFontSize}pt ${RHNavbar.topBarCompanyLabelFontFamily}`
            const companyLabelText = this.userCompanyLabel.textElementView.viewHTMLElement.textContent ?? ""
            if (!document.fonts.check(companyLabelFont, companyLabelText)) {
                document.fonts.load(companyLabelFont, companyLabelText).then(() => {
                    this.userCompanyLabel.documentFontsDidLoad()
                    this.setNeedsLayout()
                }).catch(error => console.error("Failed to load the navbar company font", error))
            }
            
            this.userNameLabel.hidden = NO
            this.userRoleLabel.hidden = NO
            this.userCompanyLabel.hidden = NO
        }
        else {
            this.userNameLabel.hidden = YES
            this.userRoleLabel.hidden = YES
            this.userCompanyLabel.hidden = YES
            this.userMenuButton.hoverText = undefined
        }
        
        this.setNeedsLayout()
    }
    
    private _updateResponsiveNavigationLabels() {
        const labels = RHResponsiveTextTokens.topBarLabels
        let publishedProcurementsLabel = labels.publishedProcurements
        if (RHCore.sharedInstance.isInOffererMode && IS(this._cachedCurrentCompanyAffiliation)) {
            publishedProcurementsLabel = labels.offererDashboard
        }
        this._applyResponsiveNavigationLabel(this.publishedProcurementsButton, publishedProcurementsLabel)
        this._applyResponsiveNavigationLabel(this.ongoingProcurementsButton, labels.ongoingProcurements)
        this._applyResponsiveNavigationLabel(this.incomingOffersButton, labels.incomingOffers)
        this._applyResponsiveNavigationLabel(this.outgoingOffersButton, labels.outgoingOffers)
        this._applyResponsiveNavigationLabel(this.statisticsButton, labels.statistics)
    }
    
    private _applyResponsiveNavigationLabel(button: RHBarLinkButton, label: { regular: string, compact: string }) {
        const responsiveLabel = labelForRHViewport(label)
        if (button.button.titleLabel.text != responsiveLabel) {
            button.button.titleLabel.text = responsiveLabel
        }
        button.button.titleLabel.hoverText = label.regular
    }
    
    
    private selectCorrectButtons() {
        const route = UIRoute.currentRoute
        
        const isUserMainRoute = IS(route.componentWithViewController(UserMainViewController))
        const isUnaffiliatedCompanyAffiliationRoute = this._isUnaffiliatedCompanyAffiliationRoute(route)
        const shouldSelectUserMainButton = isUserMainRoute || isUnaffiliatedCompanyAffiliationRoute
        const isUserMenuRoute =
            IS(route.componentWithViewController(SettingsViewController)) ||
            IS(route.componentWithViewController(CompanyViewController)) ||
            IS(route.componentWithViewController(ContractsViewController)) ||
            IS(route.componentWithViewController(HelpViewController))

        // Uses _ALL_NAV_VIEW_CONTROLLERS as the single source of truth.
        // NewProcurementPreviewViewController is included because the button
        // group maps it to newProcurementButton.
        let selectedKey = _ALL_NAV_VIEW_CONTROLLERS.find(
            vc => IS(route.componentWithViewController(vc))
        )?.routeComponentName

        if (isUnaffiliatedCompanyAffiliationRoute) {
            selectedKey = UserMainViewController.routeComponentName
        }
        else if (
            isUserMainRoute &&
            RHCore.sharedInstance.isInOffererMode &&
            IS(this._cachedCurrentCompanyAffiliation) &&
            IS(this.publishedProcurementsButton.superview)
        ) {
            selectedKey = PublishedProcurementsViewController.routeComponentName
        }

        if (isUserMenuRoute) {
            selectedKey = undefined
        }

        // Every route has one active navigation owner. Assigning an empty value
        // clears the previously selected bottom-nav button for non-nav routes.
        this.navigationButtonGroup.value = selectedKey ?? ""

        this.userMainButton.selected = shouldSelectUserMainButton && IS(this.userMainButton.superview)
        this.procurerMainButton.selected = isUserMainRoute && IS(this.procurerMainButton.superview)

        this.loginButton.selected = IS(route.componentWithViewController(LoginViewController))

        this._userMenuButtonIsActive = isUserMenuRoute
        this._applyUserMenuButtonState()

        this.bottomBarView.hidden = !RHCore.sharedInstance.isUserLoggedIn

        this.setNeedsLayout()
        
    }
    
    
    /**
     * Returns the nav button that corresponds to the given view controller.
     * Used when building the navigationButtonGroup from _ALL_NAV_VIEW_CONTROLLERS.
     */
    private _navButtonForViewController(vc: typeof _ALL_NAV_VIEW_CONTROLLERS[number]): RHBarLinkButton {
        const map: Partial<Record<string, RHBarLinkButton>> = {
            [UserMainViewController.routeComponentName]: this.userMainButton,
            [ProcurementSearchViewController.routeComponentName]: this.searchButton,
            [LogViewController.routeComponentName]: this.eventsButton,
            [NewProcurementViewController.routeComponentName]: this.newProcurementButton,
            [NewProcurementPreviewViewController.routeComponentName]: this.newProcurementButton,
            [MyOngoingProcurementsViewController.routeComponentName]: this.ongoingProcurementsButton,
            [PublishedProcurementsViewController.routeComponentName]: this.publishedProcurementsButton,
            [IncomingOffersViewController.routeComponentName]: this.incomingOffersButton,
            [OutgoingOffersViewController.routeComponentName]: this.outgoingOffersButton,
            [ContractsViewController.routeComponentName]: this.dealsButton,
            [InvoicesViewController.routeComponentName]: this.invoicesButton,
            [StatisticsMainViewController.routeComponentName]: this.statisticsButton,
            [CompanyViewController.routeComponentName]: this.companyButton
        }
        return map[vc.routeComponentName]!
    }
    
    
    /**
     * Updates the notification badges on every badged navigation button
     * from the current `RHCore.unseenCounts`.
     *
     * Runs when counts arrive with login data or with any mark-seen refresh.
     */
    private _updateAllBadges() {
        const counts = RHCore.sharedInstance.unseenCounts
        
        // Unread information is shown by bold navigation titles. Orange dots
        // are reserved for concrete unfinished actions.
        RHNotificationBadge.applyUnreadEmphasis(
            this.eventsButton.button.titleLabel,
            counts.totalEvents > 0,
            "Lugemata teavitus."
        )
        // Dashboard indicators use task/exposure state, not notification acknowledgement.
        RHNotificationBadge.applyDot(
            this.procurerMainButton.button.titleLabel,
            counts.procurementsRequiringProcurerAttention > 0 ||
            counts.procurementsRequiringQAAttention > 0,
            "Hange vajab tegevust (pakkumuste hindamine või küsimusele vastamine)."
        )
        RHNotificationBadge.applyUnreadEmphasis(
            this.outgoingOffersButton.button.titleLabel,
            counts.unreadOfferMessagesAsOfferer > 0,
            "Lugemata sõnum pakkumuse teabevahetuses."
        )
        
        this._updateInvoicesBadge()
        // New procurement exposure is independent of the notification inbox.
        RHNotificationBadge.applyUnreadEmphasis(
            this.publishedProcurementsButton.button.titleLabel,
            counts.newPublishedProcurements > 0,
            "Uus hange."
        )
        this._updateCompanyButtonBadge()
    }


    private _updateInvoicesBadge() {
        // Invoice badge intentionally stays hidden.
        RHNotificationBadge.applyUnreadEmphasis(this.invoicesButton.button.titleLabel, NO)
    }
    
    
    
    private _updateCompanyButtonBadge() {
        // Join-request badge intentionally stays hidden.
        RHNotificationBadge.applyUnreadEmphasis(this.companyButton.button.titleLabel, NO)
    }
    
    
    override didReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        
        super.didReceiveBroadcastEvent(event)
        
        if (event.name == UICore.broadcastEventName.RouteDidChange) {
            this._updateUserLabels()
            this.selectCorrectButtons()
            this.updateLogButton()
        }
        
        if (
            event.name == RHCore.broadcastEventName.userDidLogIn ||
            event.name == RHCore.broadcastEventName.userDidLogOut
        ) {
            this._updateTargetRoutesAndButtons()
            this._updateUserLabels()
            this.updateLogButton()
            this.selectCorrectButtons()
            this._updateTopSectionBackgroundForCurrentApplicationMode()
            this.setNeedsLayoutUpToRootView()
        }
        
        if (event.name == RHCore.broadcastEventName.userDidLogIn) {
            this._updateAllBadges()
        }
        
        if (event.name == RHCore.broadcastEventName.userDidLogOut) {
            this._updateAllBadges()
        }
        
        if (event.name == RHCore.broadcastEventName.unseenEventCountDidChange) {
            this._updateAllBadges()
        }
        
        if (event.name == RHCore.broadcastEventName.pendingJoinRequestCountDidChange) {
            this._updateCompanyButtonBadge()
        }
        
        if (event.name == RHCore.broadcastEventName.applicationModeDidChange) {
            this._updateUserLabels()
            this._updateTopSectionBackgroundForCurrentApplicationMode()
            const currentMode = RHCore.sharedInstance.applicationMode
            if (this._visibleApplicationModes.includes(currentMode)) {
                this._modeButtonGroup.value = currentMode
            }
            this._updateVisibleNavigationButtons(this._cachedCurrentCompanyAffiliation)
            this.selectCorrectButtons()
            this.setNeedsLayout()
        }

        if (
            event.name == RHCore.broadcastEventName.companyAffiliationsDidChange ||
            event.name == RHCore.broadcastEventName.currentCompanyAffiliationDidChange ||
            event.name == RHCore.broadcastEventName.activeCompanyDidChange
        ) {
            this._updateTargetRoutesAndButtons().then(() => {
                this._updateUserLabels()
                this.setNeedsLayoutUpToRootView()
            })
        }
        
        // isPasswordChangeRequired clears in place on the cached userProfile
        // object (no re-login), so the nav-hiding gates above need an explicit
        // nudge to recompute instead of waiting for userDidLogIn.
        if (event.name == RHCore.broadcastEventName.userProfileDidChange) {
            this._updateUserLabels()
            this._updateVisibleNavigationButtons(this._cachedCurrentCompanyAffiliation)
            this.selectCorrectButtons()
            this.setNeedsLayout()
        }

    }
    
    override didMoveToSuperview(superview: UIView) {
        super.didMoveToSuperview(superview)
        this._updateTargetRoutesAndButtons()
        this.updateLogButton()
        this.selectCorrectButtons()
        this._updateUserLabels()
        RHPageFocusManager.shared.registerTopBarManager(this._focusManager)
    }
    
    
    private _coverPageBeforeLogout() {
        const rootView = document.getElementById("RootView")
        const loadingView = RHDetachedLoadingView.heldElement()
        if (rootView && loadingView) {
            rootView.appendChild(loadingView)
        }
    }
    
    
    override layoutSubviews() {
        
        super.layoutSubviews()
        this._updateResponsiveNavigationLabels()
        
        const padding = this.core.paddingLength
        const bounds = this.bounds
        const isLoggedIn = RHCore.sharedInstance.isUserLoggedIn
        
        const topHeight: number = IF(isLoggedIn)(() => RHNavbar.topBarTopSectionHeight).ELSE(() => bounds.height)
        
        this.topSectionView.frame = bounds.rectangleWithHeight(topHeight)
        
        // Right-side control: logged-in users get the clickable user/company menu button,
        // logged-out users get the login button. The user labels live inside the menu button.
        if (isLoggedIn && IS(this.userMenuButton.superview)) {
            const userInfoLabelHeight = RHNavbar.topBarUserInfoLabelHeight
            const labelsWidth = [
                this.userNameLabel.intrinsicContentWidth(userInfoLabelHeight),
                this.userRoleLabel.intrinsicContentWidth(userInfoLabelHeight),
                this.userCompanyLabel.intrinsicContentWidth(userInfoLabelHeight)
            ].max() + RHNavbar.userMenuLabelWidthSafetyBuffer
            const userMenuButtonWidth = [
                labelsWidth
                + RHNavbar.userMenuChevronWidth
                + RHNavbar.userMenuButtonLabelToChevronGap
                + RHNavbar.userMenuButtonHorizontalInset * 2,
                RHNavbar.userMenuButtonMaxWidth
            ].min()

            this.userMenuButton.frame = this.topSectionView.bounds
                .rectangleWithInset(padding * 0.5)
                .rectangleWithWidth(userMenuButtonWidth, 1)
                .rectangleWithHeight(RHNavbar.topBarUserInfoBlockHeight + RHSpacing.tiny * 2, 0.5)
        }
        else if (this._currentLogButton) {
            this._currentLogButton.frame = this.topSectionView.bounds.rectangleWithWidth(
                    RHNavbar.topBarLogButtonWidth, 1)
                .rectangleWithInset(padding * 0.5)
                .rectangleWithHeight(RHNavbar.topBarLogButtonHeight, 0.5)
        }

        const rightControlView = IF(isLoggedIn && IS(this.userMenuButton.superview))(() =>
            this.userMenuButton as UIView | undefined
        ).ELSE(() =>
            this._currentLogButton as UIView | undefined
        )

        if (this._showsModeToggle && IS(rightControlView)) {
            const toggleButtonWidth = RHNavbar.topBarModeToggle.buttonWidth
            const toggleButtonHeight = RHNavbar.topBarModeToggle.buttonHeight
            const modeButtonsWidth = toggleButtonWidth * this._visibleModeButtons.length +
                RHNavbar.topBarModeToggle.segmentGap * (this._visibleModeButtons.length - 1)

            rightControlView!.frame
                .rectangleForPreviousColumn(
                    RHNavbar.topBarTopControlButtonGap,
                    modeButtonsWidth
                )
                .rectangleWithHeight(toggleButtonHeight, 0.5)
                .framesByDistributingViewsAsRow(
                    this._visibleModeButtons,
                    RHNavbar.topBarModeToggle.segmentGap,
                    toggleButtonWidth
                )
        }

        const leftInset = padding * 0.5
        const rightBoundary = IF(IS(rightControlView))(() =>
            IF(this._showsModeToggle)(() =>
                this._visibleModeButtons.firstElement.frame.x
            ).ELSE(() =>
                rightControlView!.frame.x
            )
        ).ELSE(() =>
            this.topSectionView.bounds.max.x
        )
        const titleText = (this.titleLabel.viewHTMLElement.textContent ?? "").trim()
        const usesCompactTitle = titleText.length >= RHNavbar.topBarTitleCompactTextLength
        this.titleLabel.configureWithObject({
            fontSize: IF(usesCompactTitle)(() =>
                RHNavbar.topBarTitleCompactFontSize
            ).ELSE(() =>
                RHNavbar.topBarTitleFontSize
            ),
            style: {
                letterSpacing: IF(usesCompactTitle)(() =>
                    RHNavbar.topBarTitleCompactLetterSpacing
                ).ELSE(() =>
                    RHNavbar.topBarTitleLetterSpacing
                )
            }
        })
        const titleTextElement = this.titleLabel.viewHTMLElement.querySelector(
            `#${this.titleLabel.elementID}_textElement`
        ) as HTMLElement | null
        const titleIntrinsicWidth = [
            this.titleLabel.intrinsicContentWidth(this.topSectionView.bounds.height),
            titleTextElement?.scrollWidth ?? 0
        ].max() + RHNavbar.topBarTitleWidthSafetyBuffer
        const titleMaxWidth = [
            rightBoundary - leftInset - padding,
            0
        ].max()
        this.titleLabel.frame = this.topSectionView.bounds.rectangleWithInset(leftInset)
            .rectangleWithWidth([
                titleIntrinsicWidth,
                titleMaxWidth
            ].min())

        if (isLoggedIn) {
            const bottomHeight = bounds.height - topHeight
            this.bottomBarView.frame = bounds.rectangleWithHeight(bottomHeight, 1)
            
            const buttonPadding = padding * 0.25
            
            const navButtons = this._visibleNavigationButtons
            
            const bottomBarContentFrame = this.bottomBarView.bounds
                .rectangleWithInset(buttonPadding)
            
            bottomBarContentFrame
                .rectangleWithInsets(0, RHNavbar.topBarFeedbackButtonWidth + buttonPadding, 0, 0)
                .IF(navButtons.length > 0)
                .distributeViewsEquallyAlongWidth(navButtons, buttonPadding)
                .ENDIF()
            
            this.feedbackButton.frame = bottomBarContentFrame
                .rectangleWithWidth(RHNavbar.topBarFeedbackButtonWidth, 1)
                .rectangleWithHeight(RHNavbar.topBarFeedbackButtonHeight, 0.5)
        }
        else {
            this.bottomBarView.frame = bounds.rectangleWithHeight(0, 1)
        }
        
    }
    
    
    override intrinsicContentHeight(constrainingWidth?: number): number {
        if (RHCore.sharedInstance.isUserLoggedIn) {
            return RHNavbar.topBarTopSectionHeight + RHNavbar.topBarBottomSectionHeight
        }
        return RHNavbar.topBarTopSectionHeight
    }
    
    
}
